import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MenuItem as MuiMenuItem,
  Grid as MuiGrid,
  TextField as MuiTextField,
} from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider as MuiLocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns as MuiAdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { format, isValid, parseISO } from "date-fns";
import { useFormikContext } from "formik";
import isEmpty from "lodash/isEmpty";
import {
  defaultFUDateFrom,
  defaultDateTo,
  SummaryAccrual,
  MultiSite,
  RecruitmentStatusOptions,
} from "app/shared/constants";
import { Autocomplete } from "app/shared/UI/Autocomplete/Autocomplete";
import { useStudies } from "app/services/studiesService";

const StudyDetailsTab = () => {
  const {
    values,
    setFieldTouched,
    setFieldValue,
    errors,
    touched,
    handleBlur,
  } = useFormikContext();

  const { t } = useTranslation();
  const { therapeuticAreas, fundingSources, allHonestBrokers } = useStudies();
  const [recruitmentStartDateError, setRecruitmentStartDateError] =
    useState(false);
  const [recruitmentEndDateError, setRecruitmentEndDateError] = useState(false);

  return !isEmpty(values) ? (
    <MuiGrid container justifyContent="space-between" spacing={2}>
      <MuiGrid size={6}>
        <MuiTextField
          fullWidth
          name="protocolDepartment"
          label={t(`formLabel.department`)}
          variant="outlined"
          value={values.protocolDeptName || ""}
          disabled
          size="small"
        />
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiTextField
          fullWidth
          name="division"
          label={t(`formLabel.division`)}
          value={values.division}
          variant="outlined"
          onBlur={handleBlur}
          onChange={(e) => {
            setFieldValue("division", e.target.value);
          }}
          size="small"
          error={touched.division && Boolean(errors.division)}
          helperText={
            touched.division &&
            errors.division &&
            t("formValidation.requiredField")
          }
        />
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiTextField
          fullWidth
          select
          label={t(`formLabel.therapeuticArea`)}
          variant="outlined"
          size="small"
          name="therapeuticArea"
          value={values.therapeuticArea ? values.therapeuticArea?.name : ""}
          onChange={(e) => {
            const matchedObj = therapeuticAreas.find(
              (item) => item.name === e.target.value
            );
            setFieldValue("therapeuticArea", matchedObj);
          }}
        >
          <MuiMenuItem value={0}>{t("formLabel.none")}</MuiMenuItem>
          {therapeuticAreas &&
            therapeuticAreas.map((item) => (
              <MuiMenuItem key={item.id} value={item.name}>
                {item.name}
              </MuiMenuItem>
            ))}
        </MuiTextField>
      </MuiGrid>
      <MuiGrid size={6}>
        <Autocomplete
          name="honestBroker"
          id="honestBroker"
          key="honest_Broker"
          size="small"
          options={allHonestBrokers || []}
          getOptionLabel={(option) =>
            option && option.displayName ? option.displayName : ""
          }
          getOptionDisabled={(option) => !option.currentHonestBroker}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          label={t("formLabel.honestBroker")}
          variant="outlined"
          margin="dense"
          value={values.honestBroker}
          onBlur={handleBlur}
          onChange={async (e, newValue) => {
            (await setFieldTouched("honestBrokerRecruitmentStatus", true)) &&
              (await setFieldValue("honestBroker", newValue ? newValue : null));

            if (
              newValue &&
              newValue?.id &&
              values.honestBrokerRecruitmentStatus ===
                RecruitmentStatusOptions[6]
            ) {
              setFieldTouched("honestBrokerRecruitmentStatus", true);
              setFieldValue(
                "honestBrokerRecruitmentStatus",
                RecruitmentStatusOptions[0]
              );
            }
          }}
          clientSide={true}
          error={touched.honestBroker && errors.honestBroker ? true : false}
          helperText={
            touched.honestBroker && errors.honestBroker
              ? t("formValidation.requiredField")
              : null
          }
        />
      </MuiGrid>

      <MuiGrid size={6}>
        <MuiTextField
          fullWidth
          name="cancerCenter"
          label={t(`formLabel.cancerCenterLabel`)}
          variant="outlined"
          value={
            values.protocolCancerCenter !== undefined
              ? values.protocolCancerCenter
                ? t("common.yesLabel")
                : t("common.noLabel")
              : ""
          }
          disabled
          size="small"
        />
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiTextField
          fullWidth
          name="studySource"
          label={t(`formLabel.studySourceLabel`)}
          variant="outlined"
          value={values.source || ""}
          disabled
          size="small"
        />
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiTextField
          fullWidth
          name="piInitiated"
          label={t(`formLabel.piInitiated`)}
          value={
            values.protocolInvestigatorInitiated
              ? t("common.yesLabel")
              : t("common.noLabel")
          }
          variant="outlined"
          disabled
          size="small"
        />
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiTextField
          fullWidth
          select
          label={t(`formLabel.fundingSource`)}
          variant="outlined"
          size="small"
          name="fundingSource"
          value={values.fundingSource ? values.fundingSource?.name : ""}
          onChange={(e) => {
            const matchedObj = fundingSources.find(
              (item) => item.name === e.target.value
            );
            setFieldValue("fundingSource", matchedObj);
          }}
        >
          <MuiMenuItem value={0}>{t("formLabel.none")}</MuiMenuItem>
          {fundingSources &&
            fundingSources.map((item) => (
              <MuiMenuItem key={item.id} value={item.name}>
                {item.name}
              </MuiMenuItem>
            ))}
        </MuiTextField>
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
          <MuiDatePicker
            name="recruitmentStartDate"
            clearable
            fullWidth
            variant="inline"
            inputVariant="outlined"
            margin="dense"
            format="MM/dd/yyyy"
            label={t("formLabel.recruitmentStartDate")}
            invalidDateMessage={t("formValidation.invalidDateFormat")}
            onBlur={handleBlur}
            id="recruitment-start-date"
            minDate={defaultFUDateFrom}
            maxDate={
              values.recruitmentEndDate
                ? parseISO(values.recruitmentEndDate)
                : defaultDateTo
            }
            value={
              values.recruitmentStartDate
                ? parseISO(values.recruitmentStartDate)
                : ""
            }
            onChange={(date, { validationError }) => {
              setFieldValue(
                "recruitmentStartDate",
                isValid(date) ? format(new Date(date), "yyyy-MM-dd") : date
              );

              if (validationError === null && date === null) {
                setRecruitmentStartDateError(t("formValidation.requiredField"));
              } else if (validationError === null) {
                setRecruitmentStartDateError(null);
              }
            }}
            onError={(reason) => {
              if (reason === "maxDate") {
                setRecruitmentStartDateError(
                  t("formValidation.maxDateMessage")
                );
              } else if (reason === "minDate") {
                setRecruitmentStartDateError(
                  t("formValidation.minDateExceededError", {
                    minDateExceeded: format(defaultFUDateFrom, "MM/dd/yyyy"),
                  })
                );
              } else if (reason === "invalidDate") {
                setRecruitmentStartDateError(
                  t("formValidation.invalidDateFormat")
                );
              } else if (!reason) {
                setRecruitmentStartDateError(null);
              } else {
                setRecruitmentEndDateError(null);
              }
            }}
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                InputLabelProps: { shrink: true },
                error: Boolean(recruitmentStartDateError),
                helperText: recruitmentStartDateError,
              },
            }}
            sx={{ width: "100%" }}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
          />
        </MuiLocalizationProvider>
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
          <MuiDatePicker
            name="recruitmentEndDate"
            fullWidth
            variant="inline"
            inputVariant="outlined"
            margin="dense"
            format="MM/dd/yyyy"
            label={t("formLabel.recruitmentEndDate")}
            minDate={
              values.recruitmentStartDate
                ? parseISO(values.recruitmentStartDate)
                : defaultFUDateFrom
            }
            maxDate={defaultDateTo}
            onBlur={handleBlur}
            invalidDateMessage={t("formValidation.invalidDateFormat")}
            value={
              values.recruitmentEndDate
                ? parseISO(values.recruitmentEndDate)
                : ""
            }
            onChange={(date, { validationError }) => {
              setFieldValue(
                "recruitmentEndDate",
                isValid(date) ? format(new Date(date), "yyyy-MM-dd") : date
              );
              if (validationError === null && date === null) {
                setRecruitmentEndDateError(t("formValidation.requiredField"));
              } else if (validationError === null) {
                setRecruitmentEndDateError(null);
              }
            }}
            onError={(reason) => {
              if (reason === "minDate") {
                setRecruitmentEndDateError(t("formValidation.minDateMessage"));
              } else if (reason === "maxDate") {
                setRecruitmentEndDateError(
                  t("formValidation.maxDateExceededError", {
                    maxDateExceeded: format(defaultDateTo, "MM/dd/yyyy"),
                  })
                );
              } else if (reason === "invalidDate") {
                setRecruitmentEndDateError(
                  t("formValidation.invalidDateFormat")
                );
              } else if (!reason) {
                setRecruitmentEndDateError(null);
              } else {
                setRecruitmentStartDateError(null);
              }
            }}
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                InputLabelProps: { shrink: true },
                error: Boolean(recruitmentEndDateError),
                helperText: recruitmentEndDateError,
              },
            }}
            sx={{ width: "100%" }}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
          />
        </MuiLocalizationProvider>
      </MuiGrid>

      <MuiGrid size={6}>
        <MuiTextField
          fullWidth
          name="protocolSummaryAccrual"
          label={t(`formLabel.summaryAccrual`)}
          value={
            values.protocolSummaryAccrual
              ? SummaryAccrual.SummaryAccrualTrue
              : SummaryAccrual.SummaryAccrualFalse || ""
          }
          variant="outlined"
          disabled
          size="small"
        />
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiTextField
          fullWidth
          name="protocolMultisiteTrial"
          label={t(`formLabel.multiSite`)}
          value={
            values.protocolMultisiteTrial
              ? MultiSite.MultiSiteTrue
              : MultiSite.MultiSiteFalse || ""
          }
          variant="outlined"
          disabled
          size="small"
        />
      </MuiGrid>
    </MuiGrid>
  ) : null;
};

export default StudyDetailsTab;
